import baseApi from './base.api'
import {
    normalizeCreateConceptualLabelResponse,
    normalizeFetchConceptualLabelsResponse,
    normalizeUpdateConceptualLabelResponse,
    normalizeFetchConceptualLabelDiscoveryResponse,
    normalizeFetchConceptualLabelUsageResponse,
} from './utils/normalizers/conceptual-label.normalizers'
import {
    handleDeleteOnQueryStarted,
} from './utils/response-handlers/manualCacheUpdateUtils'
import { handleBatchDeletePatternOnQueryStarted, handleDeletePatternOnQueryStarted } from './utils/response-handlers/discovery.api.response.handlers'
import { CONCEPTUAL_LABEL_TAG, createTagsForList } from './utils/tags'
import { flatten } from '../../utils/array.helper'
import { denormalizeConceptualLabelsMatches } from '../../pages/resources/conceptual-labels/select/ConceptualLabelSelect'
import { ConceptualLabelScopes } from '../../constants/conceptual-label-scopes'
import { ConceptualLabelDiscoveryTypes } from '../../constants/conceptual-label-discovery-types'

export const conceptualLabelApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchConceptualLabels: builder.query({
            query: (params = {}) => {
                const { scope = ConceptualLabelScopes.ALL } = params || {}
                return {
                    url: `/conceptual-labels/`,
                    method: 'GET',
                    params: { scope }
                }
            },
            transformResponse: normalizeFetchConceptualLabelsResponse,
            providesTags: result => createTagsForList(flatten(result), CONCEPTUAL_LABEL_TAG),
        }),
        fetchConceptualLabelsForVersion: builder.query({
            query: (params = {}) => {
                const { token, scope = ConceptualLabelScopes.ALL } = params || {}
                return {
                    url: `/conceptual-labels/${token}/by-version`,
                    method: 'GET',
                    params: { scope }
                }
            },
            transformResponse: normalizeFetchConceptualLabelsResponse,
            providesTags: result => createTagsForList(flatten(result), CONCEPTUAL_LABEL_TAG),
        }),
        fetchConceptualLabelDiscovery: builder.query({
            query: ({ token, disciplineId }) => ({
                url: `/conceptual-label-discovery/${token}/with-components`,
                method: 'GET',
                params: {
                    discovery_type: disciplineId ? ConceptualLabelDiscoveryTypes.CONCEPTUAL_LABEL : ConceptualLabelDiscoveryTypes.SEMANTIC,
                },
            }),
            providesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
            transformResponse: normalizeFetchConceptualLabelDiscoveryResponse,
        }),
        createConceptualLabel: builder.mutation({
            query: ({ name, disciplineId, source, description, parentId, typeId }) => ({
                url: `/conceptual-labels/`,
                method: 'POST',
                body: { name, disciplines: [disciplineId], source, description, parent_id: parentId, type_id: typeId },
            }),
            transformResponse: normalizeCreateConceptualLabelResponse,
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'LIST' }],
        }),
        updateConceptualLabel: builder.mutation({
            query: ({ id, name, disciplineId, source, description, parentId, typeId }) => ({
                url: `/conceptual-labels/${id}`,
                method: 'PATCH',
                body: { name, disciplines: [disciplineId], source, description, parent_id: parentId, type_id: typeId },
            }),
            transformResponse: normalizeUpdateConceptualLabelResponse,
            invalidatesTags: (result, error, { id }) => [{ type: CONCEPTUAL_LABEL_TAG, id }],
        }),
        deleteConceptualLabel: builder.mutation({
            query: ({ id }) => ({
                url: `/conceptual-labels/${id}`,
                method: 'DELETE',
            }),
            onQueryStarted: handleDeleteOnQueryStarted('fetchConceptualLabels'),
            invalidatesTags: (result, error, { id }) => [{ type: CONCEPTUAL_LABEL_TAG, id }],
        }),
        updateConceptualLabelDiscovery: builder.mutation({
            query: ({ patternId, ...rest }) => ({
                url: `/conceptual-label-discovery/${patternId}`,
                method: 'PATCH',
                body: {
                    ...rest,
                    conceptual_labels: rest.conceptual_labels ? denormalizeConceptualLabelsMatches(rest.conceptual_labels) : undefined
                },
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
        }),
        deleteConceptualLabelDiscovery: builder.mutation({
            query: ({ patternId }) => ({
                url: `/conceptual-label-discovery/${patternId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
            onQueryStarted: handleDeletePatternOnQueryStarted,
        }),
        batchDeleteConceptualLabelDiscovery: builder.mutation({
            query: ({ patternIds }) => ({
                url: `/conceptual-label-discovery/many`,
                method: 'DELETE',
                body: { pattern_ids: patternIds },
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
            onQueryStarted: handleBatchDeletePatternOnQueryStarted,
        }),
        projectConceptualLabelDiscovery: builder.mutation({
            query: ({ layerId, patternIds }) => ({
                url: `/conceptual-label-discovery/project/${layerId}`,
                method: 'POST',
                body: patternIds,
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
        }),
        fetchConceptualLabelUsage: builder.query({
            query: ({ conceptualLabelId }) => ({
                url: `/conceptual-labels/usage`,
                method: 'GET',
                params: { conceptual_label_id: conceptualLabelId },
            }),
            transformResponse: (response, meta, { conceptualLabelId }) => 
                normalizeFetchConceptualLabelUsageResponse(response, conceptualLabelId),
            providesTags: (result, error, { conceptualLabelId }) => [
                { type: CONCEPTUAL_LABEL_TAG, id: `USAGE-${conceptualLabelId}` }
            ],
        }),
        downloadConceptualLabelDiscoveryResults: builder.query({
            query: ({ versionId }) => ({
                url: `/conceptual-label-discovery/version/${versionId}/latest-results`,
                method: 'GET',
                responseType: 'blob',
            }),
        }),
    }),
})

export const {
    useFetchConceptualLabelsQuery,
    useFetchConceptualLabelDiscoveryQuery,
    useFetchAllConceptualLabelDiscoveryQuery,
    useFetchConceptualLabelsForVersionQuery,
    useCreateConceptualLabelMutation,
    useUpdateConceptualLabelMutation,
    useDeleteConceptualLabelMutation,
    useUpdateConceptualLabelDiscoveryMutation,
    useDeleteConceptualLabelDiscoveryMutation,
    useBatchDeleteConceptualLabelDiscoveryMutation,
    useProjectConceptualLabelDiscoveryMutation,
    useFetchConceptualLabelUsageQuery,
    useLazyDownloadConceptualLabelDiscoveryResultsQuery,
} = conceptualLabelApi
